import React, { useState } from 'react';
import {
  Box, Button, Container, Grid, Typography, Card, CardContent,
  AppBar, Toolbar, Stack, Chip, Avatar, MenuItem, Select,
  FormControl, InputLabel, Paper, Divider, useTheme, useMediaQuery,
  Table, TableHead, TableRow, TableCell,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VerifiedIcon from '@mui/icons-material/Verified';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useCompanyDetails } from '../utils/companyDetails';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PROFILE_FOR_OPTIONS = ['Myself', 'Daughter', 'Son', 'Sister', 'Brother', 'Relative', 'Friend'];

const STATS = [
  { value: '5 Lakh+', label: 'Verified Profiles',  icon: <VerifiedIcon sx={{ fontSize: 32, color: '#fff' }} /> },
  { value: '50,000+', label: 'Successful Matches',  icon: <EmojiEventsIcon sx={{ fontSize: 32, color: '#fff' }} /> },
  { value: '26 Yrs',  label: 'Trusted Service',     icon: <StarIcon sx={{ fontSize: 32, color: '#fff' }} /> },
  { value: '100%',    label: 'Secure & Private',    icon: <SecurityIcon sx={{ fontSize: 32, color: '#fff' }} /> },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Create Your Profile',  desc: 'Register and fill in personal, educational, family and cultural details. Upload a photo and set partner preferences.' },
  { step: '02', title: 'Browse Matches',        desc: 'Smart engine surfaces profiles matching your community, location, education and lifestyle preferences.' },
  { step: '03', title: 'Send Interest',         desc: 'Shortlist profiles you like and send an interest request. Both families get notified.' },
  { step: '04', title: 'Connect & Celebrate',  desc: 'Once interests align, families connect and formalise with digital contracts.' },
];

const FEATURES = [
  { icon: <FavoriteIcon sx={{ fontSize: 36 }} />,    color: '#e91e63', title: 'Smart Matchmaking',   desc: 'Filter by community, religion, star, location, age, education and income. Compatibility ranking built-in.' },
  { icon: <VerifiedIcon sx={{ fontSize: 36 }} />,    color: '#1565c0', title: 'Verified Profiles',   desc: 'Mobile-verified profiles. Photo verification and horoscope matching ensure genuine families only.' },
  { icon: <DescriptionIcon sx={{ fontSize: 36 }} />, color: '#7b1fa2', title: 'Digital Contracts',   desc: 'Legally-structured family agreements with e-signature and penalty clause support.' },
  { icon: <ChecklistIcon sx={{ fontSize: 36 }} />,   color: '#e65100', title: 'Family Wishlists',    desc: "Each family maintains their own checklists, cultural requirements and budget parameters." },
  { icon: <SecurityIcon sx={{ fontSize: 36 }} />,    color: '#2e7d32', title: 'Privacy First',       desc: 'Contact details hidden until both families consent. End-to-end encrypted. GDPR compliant.' },
  { icon: <PeopleIcon sx={{ fontSize: 36 }} />,      color: '#6a1b9a', title: 'Multi-Role Access',   desc: 'Separate portals for families, matchmaker, and vendors — everyone sees only what is relevant.' },
];

const SUCCESS_STORIES = [
  { names: 'Priya & Karthik', community: 'Iyer',    location: 'Chennai',    year: '2025', quote: "We found each other here. The matchmaker coordinated between our families effortlessly." },
  { names: 'Deepa & Aravind', community: 'Iyengar', location: 'Bengaluru',  year: '2025', quote: "The digital contract gave both families complete clarity. Absolutely recommended!" },
  { names: 'Meena & Suresh',  community: 'Pillai',  location: 'Coimbatore', year: '2024', quote: "The wishlist feature tracked every family member's expectations in one place. Brilliant." },
];

export default function HomePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [profileFor, setProfileFor] = useState('Myself');
  const { user } = useAuth();
  const isAdmin = user?.role?.name === 'system_admin';
  const COMPANY = useCompanyDetails();

  return (
    <Box sx={{ bgcolor: 'background.default' }}>

      {/* ── Nav ── */}
      <AppBar position="sticky" color="primary" elevation={0}>
        <Toolbar sx={{ maxWidth: 1200, mx: 'auto', width: '100%', px: { xs: 2, md: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <FavoriteIcon sx={{ color: '#f9a825' }} />
            <Typography variant="h6" fontWeight={800} color="white">{COMPANY.brandName}</Typography>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button component={RouterLink} to="/login" variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}>Login</Button>
            <Button component={RouterLink} to="/signup" variant="contained" color="secondary">
              Register Free
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ── Hero ── */}
      <Box sx={{ background: 'linear-gradient(135deg,#4a0072 0%,#7b1fa2 50%,#ae52d4 100%)', pt: { xs: 6, md: 10 }, pb: { xs: 6, md: 10 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', top: -100, right: -100 }} />
        <Box sx={{ position: 'absolute', width: 250, height: 250, borderRadius: '50%', bgcolor: 'rgba(249,168,37,0.08)', bottom: -60, left: -60 }} />
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip label="Trusted by 5 Lakh+ Families" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', mb: 2 }} />
              <Typography variant={isMobile ? 'h3' : 'h2'} fontWeight={800} color="white" lineHeight={1.2} mb={2}>
                The Most Trusted<br />
                <Box component="span" sx={{ color: '#f9a825' }}>Tamil Matrimony</Box><br />
                Service
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, fontWeight: 400, lineHeight: 1.7 }}>
                Find your perfect life partner with cultural compatibility, family-first matching, and complete privacy.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button variant="contained" color="secondary" size="large"
                  sx={{ fontWeight: 700, px: 4, py: 1.6 }} onClick={() => navigate('/signup')}>
                  Register Free
                </Button>
                <Button variant="outlined" size="large"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', px: 4, py: 1.6 }}
                  onClick={() => navigate('/login')}>
                  Partner Login
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={8} sx={{ borderRadius: 4, p: 4 }}>
                <Typography variant="h6" fontWeight={700} mb={0.5}>Find your perfect match</Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Create a free profile — takes 2 minutes
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Profile created for</InputLabel>
                  <Select value={profileFor} label="Profile created for" onChange={e => setProfileFor(e.target.value)}>
                    {PROFILE_FOR_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="contained" color="primary" fullWidth size="large"
                  sx={{ fontWeight: 700, py: 1.5 }} onClick={() => navigate('/signup')}>
                  Register Free
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1.5}>
                  By registering, I agree to the Terms & Privacy Policy
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Stats ── */}
      <Box sx={{ background: 'linear-gradient(90deg,#7b1fa2,#4a0072)', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {STATS.map(s => (
              <Grid item xs={6} md={3} key={s.label}>
                <Stack alignItems="center" spacing={1}>
                  {s.icon}
                  <Typography variant="h5" fontWeight={800} color="white">{s.value}</Typography>
                  <Typography variant="caption" color="rgba(255,255,255,0.8)">{s.label}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How it Works ── */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h4" fontWeight={800} mb={1}>How It Works</Typography>
            <Typography variant="body1" color="text.secondary">Your journey to the perfect match in 4 simple steps</Typography>
          </Box>
          <Grid container spacing={4}>
            {HOW_IT_WORKS.map(step => (
              <Grid item xs={12} sm={6} md={3} key={step.step}>
                <Stack alignItems="center" textAlign="center">
                  <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={800} color="white">{step.step}</Typography>
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700} mb={1}>{step.title}</Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{step.desc}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Features ── */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h4" fontWeight={800} mb={1}>Platform Features</Typography>
            <Typography variant="body1" color="text.secondary">Built for Tamil families — culturally rich, privacy-first</Typography>
          </Box>
          <Grid container spacing={3}>
            {FEATURES.map(f => (
              <Grid item xs={12} sm={6} md={4} key={f.title}>
                <Card elevation={2} sx={{ borderRadius: 3, height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: 2.5, bgcolor: `${f.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: f.color, mb: 2 }}>
                      {f.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} mb={1}>{f.title}</Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{f.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Success Stories ── */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#f3e5f5' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip label="Real Success Stories" color="primary" sx={{ mb: 2 }} />
            <Typography variant="h4" fontWeight={800} mb={1}>Lakhs of Happy Marriages</Typography>
            <Typography variant="body1" color="text.secondary">Featured in Limca Book of Records for highest number of documented marriages online</Typography>
          </Box>
          <Grid container spacing={3}>
            {SUCCESS_STORIES.map(s => (
              <Grid item xs={12} md={4} key={s.names}>
                <Card elevation={3} sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <FormatQuoteIcon sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="body1" color="text.secondary" lineHeight={1.8} mb={3} fontStyle="italic">
                      "{s.quote}"
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>{s.names[0]}</Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>{s.names}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.community} · {s.location} · Married {s.year}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Contact Us (admin only) ── */}
      {isAdmin && <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" fontWeight={800} mb={1}>Contact Us</Typography>
            <Typography variant="body1" color="text.secondary">Reach out to us — we are here to help you find your perfect match</Typography>
          </Box>
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden', maxWidth: 900, mx: 'auto' }}>
            <Box sx={{ bgcolor: '#1a237e', px: 3, py: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color="white" letterSpacing={1}>
                {COMPANY.name}
              </Typography>
            </Box>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                  <TableCell><LocationOnIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />Address</TableCell>
                  <TableCell><PhoneIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />Phone</TableCell>
                  <TableCell><EmailIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />Email</TableCell>
                  <TableCell><LanguageIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />Website</TableCell>
                  <TableCell>GSTIN</TableCell>
                  <TableCell>CIN</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{COMPANY.address}</TableCell>
                  <TableCell>{COMPANY.phone}</TableCell>
                  <TableCell><a href={`mailto:${COMPANY.email}`} style={{ color: 'inherit' }}>{COMPANY.email}</a></TableCell>
                  <TableCell><a href={`https://${COMPANY.website}`} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{COMPANY.website}</a></TableCell>
                  <TableCell>{COMPANY.gstin}</TableCell>
                  <TableCell>{COMPANY.cin}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Container>
      </Box>}

      {/* ── CTA ── */}
      <Box sx={{ background: 'linear-gradient(135deg,#4a0072,#e91e63)', py: { xs: 5, md: 8 } }}>
        <Container maxWidth="md">
          <Stack alignItems="center" textAlign="center" spacing={3}>
            <FavoriteIcon sx={{ fontSize: 48, color: '#f9a825' }} />
            <Typography variant="h4" fontWeight={800} color="white">Start Your Journey Today</Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>
              Join 5 Lakh+ families who found happiness through our platform
            </Typography>
            <Button variant="contained" color="secondary" size="large"
              sx={{ fontWeight: 700, px: 6, py: 1.8, fontSize: '1.1rem' }}
              onClick={() => navigate('/signup')}>
              Register Free Now
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ bgcolor: '#1a0033', py: 4 }}>
        <Container maxWidth="lg">
          {isAdmin && (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FavoriteIcon sx={{ color: '#f9a825', fontSize: 18 }} />
                  <Typography variant="body2" color="rgba(255,255,255,0.9)" fontWeight={700}>{COMPANY.name}</Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LocationOnIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }} />
                    <Typography variant="caption" color="rgba(255,255,255,0.6)">{COMPANY.address}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PhoneIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }} />
                    <Typography variant="caption" color="rgba(255,255,255,0.6)">{COMPANY.phone}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <EmailIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }} />
                    <Typography variant="caption" color="rgba(255,255,255,0.6)">{COMPANY.email}</Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
            </>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="body2" color="rgba(255,255,255,0.7)">© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</Typography>
            <Stack direction="row" spacing={2}>
              {[['/legal/privacy', 'Privacy Policy'], ['/legal/terms', 'Terms & Conditions'], ['/legal/refunds', 'Refunds'], ['/legal/vendor-terms', 'Vendor Agreement'], ['/legal/notices', 'Legal Notices & Grievance Officer']].map(([to, l]) => (
                <Typography key={l} component={RouterLink} to={to} variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', '&:hover': { color: 'white' } }}>{l}</Typography>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
